import { BeneficiaryFormState } from '@app/domain/model/policy/BeneficiaryFormState';
import { BeneficiaryData } from '@app/domain/model/policy/BeneficiaryData';
import { useCallback, useMemo, useRef, useState } from 'react';

interface UseBeneficiaryActionsParams {
    beneficiaryFormState: BeneficiaryFormState;
    totalExistingShare: number;
}

export function useBeneficiaryActions({ beneficiaryFormState, totalExistingShare }: UseBeneficiaryActionsParams) {
    const [isEditingBeneficiary, setIsEditingBeneficiary] = useState(false);
    const [editingBeneficiaryId, setEditingBeneficiaryId] = useState<string | null>(null);
    const [saveAttempted, setSaveAttempted] = useState(false);
    const beneficiaryBeforeEditRef = useRef<Nullable<BeneficiaryData>>(null);

    const handleSetIsEditingBeneficiary = useCallback((isEditing: boolean) => {
        setIsEditingBeneficiary(isEditing);
    }, []);

    const handleAddBeneficiary = useCallback(() => {
        beneficiaryFormState.addBeneficiary(totalExistingShare);
        setIsEditingBeneficiary(true);
        setEditingBeneficiaryId(null);
        setSaveAttempted(false);
    }, [beneficiaryFormState, totalExistingShare]);

    const handleEditBeneficiary = useCallback(
        (beneficiaryId: string) => {
            beneficiaryBeforeEditRef.current = beneficiaryFormState.getBeneficiaryById(beneficiaryId);
            setIsEditingBeneficiary(true);
            setEditingBeneficiaryId(beneficiaryId);
            setSaveAttempted(false);
        },
        [beneficiaryFormState],
    );

    const handleCancelBeneficiaryEdit = useCallback(() => {
        if (editingBeneficiaryId === null) {
            const lastBeneficiary = beneficiaryFormState.beneficiaries[beneficiaryFormState.beneficiaries.length - 1];
            if (lastBeneficiary) {
                beneficiaryFormState.removeBeneficiary(lastBeneficiary.id);
            }
        } else {
            if (beneficiaryBeforeEditRef.current) {
                beneficiaryFormState.updateBeneficiary(editingBeneficiaryId, beneficiaryBeforeEditRef.current);
            }
        }
        beneficiaryBeforeEditRef.current = null;
        setIsEditingBeneficiary(false);
        setEditingBeneficiaryId(null);
        setSaveAttempted(false);
    }, [editingBeneficiaryId, beneficiaryFormState]);

    const editingBeneficiary = useMemo(() => {
        if (editingBeneficiaryId === null) {
            return beneficiaryFormState.beneficiaries[beneficiaryFormState.beneficiaries.length - 1] || null;
        }
        return beneficiaryFormState.getBeneficiaryById(editingBeneficiaryId) || null;
    }, [editingBeneficiaryId, beneficiaryFormState]);

    const handleSaveBeneficiary = useCallback(() => {
        setSaveAttempted(true);

        if (!editingBeneficiary) {
            setIsEditingBeneficiary(false);
            setEditingBeneficiaryId(null);
            setSaveAttempted(false);
            return;
        }

        const beneficiaryErrors = beneficiaryFormState.validation.errors[editingBeneficiary.id];

        const hasErrors =
            beneficiaryErrors &&
            (beneficiaryErrors.firstName != null ||
                beneficiaryErrors.lastName != null ||
                beneficiaryErrors.personalCode != null ||
                beneficiaryErrors.share != null ||
                beneficiaryErrors.address != null);

        if (hasErrors) {
            return;
        }

        setIsEditingBeneficiary(false);
        setEditingBeneficiaryId(null);
        setSaveAttempted(false);
    }, [editingBeneficiary, beneficiaryFormState.validation.errors]);

    return {
        isEditingBeneficiary,
        editingBeneficiaryId,
        handleSetIsEditingBeneficiary,
        saveAttempted,
        editingBeneficiary,
        handleAddBeneficiary,
        handleEditBeneficiary,
        handleCancelBeneficiaryEdit,
        handleSaveBeneficiary,
    };
}
